import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { axiosInstance } from '../../../helpers/api_helper';
import { CalculationStatus, PackageStatus, UserPackages } from './types';
import {
  formatDecimalNumber,
  handleValidDate,
  handleValidTime,
  usdFormatter
} from '../../../helpers/helpers';

export const getTravelutionBundleSubcriptions = createAsyncThunk(
  'getTravelutionBundleSubcriptions/fetch',
  async (params: URLSearchParams) => {
    const response = await axiosInstance.get(
      '/products/travelution-subscription-bundles/orders',
      {
        params
      }
    );

    return response.data.data;
  }
);

export const updateBundleSubscriptionStatus = createAsyncThunk(
  'packageOrders/updateBundleSubscriptionStatus',
  async (
    { id, calculationStatus }: { id: number; calculationStatus: number },
    { rejectWithValue }
  ) => {
    try {
      await axiosInstance.post(
        `/products/travelution-subscription-bundles/orders/${id}/confirm?calculationStatus=${calculationStatus}`
      );
      toast.success('Successfully updated Travelution Bundle Subscription');

      return id;
    } catch (error: any) {
      toast.error(error.response.data.error);
      return rejectWithValue(error.response.data.error);
    }
  }
);

export const exportPackageOrders = createAsyncThunk(
  'packageOrders/exportPackageOrders',
  async (params: URLSearchParams) => {
    const response = await axiosInstance.get(
      `/products/united-one-entry-packages/orders/export`,
      {
        params
      }
    );

    const dataForExport = response.data.data.orders.map((po: UserPackages) => ({
      orderId: po?.id,
      userId: po?.user?.id,
      user: `${po?.user?.name} ${po?.user?.surname}`,
      packageName: po?.package?.name,
      amount: po?.package?.price
        ? `$${formatDecimalNumber(po?.package?.price)}`
        : '',
      paymentAddress: po?.paymentAddress || '',
      orderDate: po?.dateCreated
        ? `${handleValidDate(po?.dateCreated)}, ${handleValidTime(
            po?.dateCreated
          )}`
        : '',
      paymentDate: po?.datePaid
        ? `${handleValidDate(po?.datePaid)}, ${handleValidTime(po?.datePaid)}`
        : '',

      status: statusForTable(po?.status, po?.calculationStatus)
    }));

    return dataForExport;
  }
);

export const exportHeaders = [
  { label: 'Order ID', key: 'orderId' },
  { label: 'User ID', key: 'userId' },
  { label: 'User', key: 'user' },
  { label: 'Package Name', key: 'packageName' },
  { label: 'Amount', key: 'amount' },
  { label: 'Payment Address', key: 'paymentAddress' },
  { label: 'Order Date', key: 'orderDate' },
  { label: 'Payment Date', key: 'paymentDate' },
  { label: 'Lock Date', key: 'lockDate' },
  { label: 'Status', key: 'status' }
];

export const statusForTable = (status: number, calculationStatus: number) => {
  switch (status) {
    case 1:
      return 'PENDING';
    case 2:
      return 'PAID';
    case 3:
      return 'CANCELED';
    case 4:
      return 'CANCELED BY USER';

    default:
      break;
  }
};

export interface PackageRankOrdersState {
  data: UserPackages[];
  loading: boolean;
  totalCount: number;
  dataForExport: UserPackages[];
  loadingExport: boolean;
}

const initialState = {
  data: [],
  totalCount: 0,
  loading: false,
  dataForExport: [],
  loadingExport: false
} as PackageRankOrdersState;

const packageOrdersSlice = createSlice({
  name: 'packageOrders',
  initialState,
  reducers: {
    removePackageOrdersForExport(state) {
      state.dataForExport = [];
    }
    // fill in primary logic here
  },
  extraReducers: (builder) => {
    //BUNDLE SUBSCRIPTIONS
    builder.addCase(getTravelutionBundleSubcriptions.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      getTravelutionBundleSubcriptions.fulfilled,
      (state, { payload }) => {
        state.loading = false;
        state.data = payload.orders;
        state.totalCount = payload.count;
      }
    );
    builder.addCase(getTravelutionBundleSubcriptions.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(updateBundleSubscriptionStatus.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(
      updateBundleSubscriptionStatus.fulfilled,
      (state, { payload }) => {
        state.loading = false;
        const index = state.data.findIndex((p) => p.id === payload);
        state.data[index] = {
          ...state.data[index],
          status: 2
        };
      }
    );
    builder.addCase(updateBundleSubscriptionStatus.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(exportPackageOrders.pending, (state) => {
      state.loadingExport = true;
    });
    builder.addCase(exportPackageOrders.fulfilled, (state, { payload }) => {
      state.loadingExport = false;
      state.dataForExport = payload;
    });
    builder.addCase(exportPackageOrders.rejected, (state) => {
      state.loadingExport = false;
    });
  }
});

export const { removePackageOrdersForExport } = packageOrdersSlice.actions;

export default packageOrdersSlice.reducer;
